import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { HelmetProvider } from 'react-helmet-async'
import './index.css'
import './i18n'
import App from './App.tsx'
import { AuthProvider } from './features/auth/AuthContext'
import { ColorSchemeProvider } from './features/landing/ColorSchemeContext'
import { ConfirmDialogProvider } from './components/ui/ConfirmDialog'
import { ErrorToastHost, reportUnexpectedError } from './components/ui/ErrorToast'
import { getApiErrorMessage } from './lib/api'
import { MessagesSocketProvider } from './features/messages/MessagesSocketContext'

// Le service worker PWA précédent précachait le shell JS/HTML : les utilisateurs qui
// l'avaient installé se retrouvaient bloqués sur un vieux shell référençant des chunks
// supprimés aux déploiements suivants (MIME text/html au lieu de JS). On désinscrit ici
// tout SW restant de cette ancienne génération chez les utilisateurs déjà piégés, puis on
// recharge une seule fois pour repartir sur une requête réseau propre.
// push-sw.js est épargné : c'est un SW volontaire et minimal (aucun cache, aucune
// interception de fetch — voir public/push-sw.js), utilisé uniquement pour les
// notifications push, sans rapport avec le problème de shell figé.
const RELOAD_GUARD_KEY = 'sw-cleanup-reload-once'
const PUSH_SW_PATH = '/push-sw.js'

// Vérifie active/installing/waiting, pas seulement active : juste après un enregistrement
// (registration.register('/push-sw.js')), le SW est encore en 'installing' pendant quelques
// millisecondes et registration.active vaut null — le filtre précédent le désinscrivait donc
// par erreur, avant même qu'il n'ait eu le temps de servir un seul événement push.
function scriptUrlOf(registration: ServiceWorkerRegistration): string | undefined {
  return (registration.active ?? registration.waiting ?? registration.installing)?.scriptURL
}

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    const staleRegistrations = registrations.filter(
      (registration) => !scriptUrlOf(registration)?.endsWith(PUSH_SW_PATH),
    )
    if (staleRegistrations.length === 0) return
    Promise.all(staleRegistrations.map((registration) => registration.unregister())).then(() => {
      if ('caches' in window) {
        caches.keys().then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
      }
      if (!sessionStorage.getItem(RELOAD_GUARD_KEY)) {
        sessionStorage.setItem(RELOAD_GUARD_KEY, '1')
        window.location.reload()
      }
    })
  })
}

// Filet de sécurité : beaucoup de mutations n'ont pas de onError local (voir audit admin),
// une erreur (403, validation, conflit FK...) échouait alors sans aucun retour visuel.
// Ce handler global ne se substitue jamais à un onError déjà défini sur la mutation —
// mutation.options.onError est déjà exécuté par React Query avant ce hook, indépendamment.
const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.onError) return
      reportUnexpectedError(getApiErrorMessage(error, 'Une erreur est survenue.'))
    },
  }),
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <MessagesSocketProvider>
              <ColorSchemeProvider>
                <ConfirmDialogProvider>
                  <App />
                  <ErrorToastHost />
                </ConfirmDialogProvider>
              </ColorSchemeProvider>
            </MessagesSocketProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  </StrictMode>,
)
